import React from 'react';
import { FaPhone, FaEnvelope } from 'react-icons/fa';
import teamPhoto1 from '../components/assets/Team/IMG_3784.jpeg';
import teamPhoto2 from '../components/assets/Team/IMG_3794.jpg';
import SEO from '../components/SEO.jsx';

const AboutUs = () => {
  const contactDetails = [
    {
      id: 1,
      name: "Tomasz Chmiel",
      role: "Co-Founder & Lead Bioresonance Therapist",
      phone: "0776 137 4278",
      email: "info@bioresonance-leicester.co.uk",
      image: teamPhoto1,
      description: "Co-founder of Bioresonance Leicester since 2017, Tomasz is passionate about helping others through holistic methods. With years of education, hands-on experience, and continuous learning, he specializes in personalized bioresonance therapy tailored to each individual's unique needs.",
      qualifications: ["Certified BICOM Bioresonance Device Training", "Cupping Therapy Certification", "Swedish Massage Certification", "Mental Health Specialty Module (NCSCT)"]
    },
    {
      id: 2,
      name: "Pawel Sieczka",
      role: "Co-Founder & Senior Bioresonance Specialist",
      phone: "0792 382 5242",
      email: "info@bioresonance-leicester.co.uk",
      image: teamPhoto2,
      description: "Co-founder of Bioresonance Leicester since 2017, Pawel brings dedication and care to every client interaction. His extensive training includes specialist work in holistic diagnosis and support for autoimmune diseases, with regular participation in international bioresonance congresses.",
      qualifications: ["Certified BICOM Bioresonance Device Training", "Assessment of Core Knowledge and Practice Skills (NCSCT)", "Mental Health Specialty Module (NCSCT)", "Specialist Training in Autoimmune Diseases (Germany)"]
    }
  ];

  return (
    <div className="pt-20 min-h-screen bg-white">
      <SEO
        title="About Us"
        description="Meet the team at Bioresonance Leicester – Tomasz and Pawel, BICOM-certified practitioners with over 7 years of experience. Complementary wellness at 108 New Walk, Leicester."
        path="/about-us"
      />
      <div className="container mx-auto px-4 py-16">
        {/* Header Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
            About Us
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Bioresonance Leicester was founded in 2017 by Tomasz Chmiel and Pawel Sieczka — two passionate individuals driven by the desire to help others through holistic methods. Since then, we've been offering Bioresonance Therapy services to our clients with dedication and care.
          </p>
        </div>

        {/* Contact Details Section - Matching TeamSection Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {contactDetails.map((contact, index) => (
            <div 
              key={contact.id} 
              className={`flex flex-col ${index % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-start gap-8`}
            >
              {/* Image - Exact same as TeamSection */}
              <div className="w-full lg:w-1/2 flex items-start">
                <div className="relative group w-full">
                  {/* Decorative background shapes */}
                  <div className="absolute -top-4 -right-4 w-24 h-24 bg-gold/10 rounded-full opacity-60 group-hover:scale-110 transition-transform duration-500"></div>
                  <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-gold2/20 rounded-full opacity-40 group-hover:scale-125 transition-transform duration-700"></div>
                  
                  {/* Main image container with creative border */}
                  <div className="relative p-4">
                    <div className="relative overflow-hidden rounded-3xl">
                      <img
                        src={contact.image}
                        alt={contact.name}
                        className="w-full h-96 object-cover object-[center_20%] transform group-hover:scale-110 transition-all duration-500"
                      />
                      
                      {/* Gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      
                      {/* Contact info overlay on hover */}
                      <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                        {/* Text background for better readability */}
                        <div className="bg-black/70 backdrop-blur-sm rounded-lg p-4 border border-gold/30">
                          <h4 className="text-white text-xl font-bold mb-2 text-center" style={{ fontFamily: "Playfair Display, serif", textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
                            {contact.name}
                          </h4>
                          <p className="text-gold text-sm font-semibold text-center border-t border-gold/50 pt-2" style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}>
                            {contact.role}
                          </p>
                          {/* Contact Information in overlay */}
                          <div className="mt-3 space-y-2">
                            <div className="flex items-center justify-center space-x-2">
                              <FaPhone className="text-gold text-sm" />
                              <a 
                                href={`tel:${contact.phone}`}
                                className="text-white hover:text-gold transition-colors duration-300 text-sm"
                              >
                                {contact.phone}
                              </a>
                            </div>
                            <div className="flex items-center justify-center space-x-2">
                              <FaEnvelope className="text-gold text-sm" />
                              <a 
                                href={`mailto:${contact.email}`}
                                className="text-white hover:text-gold transition-colors duration-300 text-xs"
                              >
                                {contact.email}
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Creative border with gold accent */}
                    <div className="absolute inset-0 border-4 border-gold/30 rounded-3xl transform rotate-3 group-hover:rotate-0 transition-transform duration-500"></div>
                    <div className="absolute inset-2 border-2 border-white/50 rounded-3xl transform -rotate-1 group-hover:rotate-0 transition-transform duration-500"></div>
                  </div>
                  
                  {/* Floating elements */}
                  <div className="absolute top-8 right-8 w-3 h-3 bg-gold rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-100"></div>
                  <div className="absolute bottom-8 left-8 w-2 h-2 bg-gold2 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-200"></div>
                </div>
              </div>

              {/* Content - Similar to TeamSection content */}
              <div className="w-full lg:w-1/2 space-y-6 flex flex-col justify-start">
                <div>
                  <h3 className="text-2xl font-bold text-dark-grey mb-2" style={{ fontFamily: "Playfair Display, serif" }}>
                    {contact.name}
                  </h3>
                  <p className="text-lg text-gold font-semibold mb-4">
                    {contact.role}
                  </p>
                  <p className="text-gray-600 leading-relaxed">
                    {contact.description}
                  </p>
                </div>

                {/* Qualifications */}
                <div className="space-y-3">
                  <h4 className="text-lg font-semibold text-dark-grey">Qualifications:</h4>
                  <ul className="space-y-2">
                    {contact.qualifications.map((qualification, idx) => (
                      <li key={idx} className="flex items-center text-gray-600">
                        <svg className="w-4 h-4 text-gold mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        {qualification}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Decorative element */}
                <div className="w-16 h-1 bg-gradient-to-r from-gold to-yellow rounded-full"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Our Story Section */}
        <div className="mt-16">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-dark-grey mb-6 text-center" style={{ fontFamily: "Playfair Display, serif" }}>
              Our Journey
            </h2>
            <p className="text-gray-600 leading-relaxed mb-12 text-center max-w-4xl mx-auto">
              What began as a shared passion has grown into a professional practice supported by years of education, hands-on experience, and continuous learning. We have completed numerous certified courses and seminars to ensure that the support we offer is both safe and tailored to each individual's unique needs.
            </p>
            
            {/* Our Qualifications */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 border-l-4 border-gold hover:shadow-xl transition-all duration-300">
              <h3 className="text-2xl font-bold text-dark-grey mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
                Our Qualifications
              </h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-gold mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Certified training in the use of the BICOM Bioresonance device</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-gold mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Introduction to Bioresonance Therapy</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-gold mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Cupping Therapy</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-gold mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Swedish Massage</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-gold mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Assessment of Core Knowledge and Practice Skills (Certificate No. 321430-326955, completed through the National Centre for Smoking Cessation and Training – NCSCT, issued 12th September 2024 by Dr Andy McEwen)</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-gold mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Mental Health Specialty Module (Certificate No. 321430-327487, completed through the NCSCT, issued 18th September 2024 by Dr Andy McEwen)</span>
                </li>
              </ul>
            </div>

            {/* Courses & Seminars */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 border-l-4 border-gold hover:shadow-xl transition-all duration-300">
              <h3 className="text-2xl font-bold text-dark-grey mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
                Courses & Seminars Completed
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <svg className="w-4 h-4 text-gold mr-2 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Rehabilitation and Physiotherapy</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-4 h-4 text-gold mr-2 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Anatomy and Physiology</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-4 h-4 text-gold mr-2 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Supporting the immune system, depression, and addiction recovery</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-4 h-4 text-gold mr-2 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Bioresonance data analysis</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-4 h-4 text-gold mr-2 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Bioinformational Therapy applications</span>
                  </li>
                </ul>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <svg className="w-4 h-4 text-gold mr-2 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Identification and interpretation of informational fields</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-4 h-4 text-gold mr-2 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Practical Naturopathy</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-4 h-4 text-gold mr-2 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>TFP (Therapy using personal frequency patterns) and Allergy Treatment</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-4 h-4 text-gold mr-2 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Specialist training in holistic diagnosis and support for autoimmune diseases (Germany)</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-4 h-4 text-gold mr-2 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Regular participation in international bioresonance congresses and seminars (Germany)</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Our Approach */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border-l-4 border-gold hover:shadow-xl transition-all duration-300">
              <h3 className="text-2xl font-bold text-dark-grey mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
                Our Approach
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                These skills allow us to closely monitor your body's response to therapy in real time and adjust the process accordingly to better support your healing journey.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                We believe that each person is unique. Even when facing similar symptoms, the path to wellness should be personalised. That's why we focus on the whole person, not just the condition — taking into account lifestyle, environment, and emotional wellbeing.
              </p>
              <p className="text-gray-600 leading-relaxed">
                We work exclusively with EU-certified medical devices, which are regularly maintained and updated for accuracy and safety.
              </p>
            </div>
          </div>
        </div>

        {/* Back to Top Button */}
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 bg-gold hover:bg-gold/90 text-black p-3 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110 z-50"
          aria-label="Back to top"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default AboutUs;
